import React, { useState, useEffect } from "react";
import { Lock } from "lucide-react";
import SurveyService from "../lib/SurveyService";
import { useAuth } from "../hooks/useAuth";
import { useSurveyTracking, useInteractionTracking, useErrorTracking } from "../hooks/useAnalytics";

const Survey = ({ onComplete, onReset, onBack }) => {
  // Survey ID
  const [surveyId] = useState("STEM_yEZSuvbG2w");

  // Authentication hook for token
  const { getIdToken, user } = useAuth();

  // Analytics hooks
  const { trackSurveyStart, trackSurveyQuestion, trackSurveyComplete, trackSurveyAbandonment } = useSurveyTracking();
  const { trackButtonClick } = useInteractionTracking();
  const { trackError } = useErrorTracking();

  // Current question state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
    const savedIndex = sessionStorage.getItem("surveyCurrentQuestionIndex");
    return savedIndex ? parseInt(savedIndex, 10) : 0;
  });

  // Survey data (fetched from server)
  const [surveyData, setSurveyData] = useState(null);

  // Questions sorted by display order
  const [sortedQuestions, setSortedQuestions] = useState([]);

  // User answers to questions
  const [answers, setAnswers] = useState(() => {
    const savedAnswers = sessionStorage.getItem("surveyData");
    return savedAnswers ? JSON.parse(savedAnswers) : {};
  });

  // Add a state variable to track if the survey is read-only
  const [isReadOnly, setIsReadOnly] = useState(false);
  // Add a state to store previous responses data
  const [previousResponses, setPreviousResponses] = useState(null);

  // UI states
  const [showThankYou, setShowThankYou] = useState(() => {
    // Check if we already stored the showThankYou state
    const savedThankYou = sessionStorage.getItem("surveyShowThankYou");

    // Also check if we have the answer data that would trigger the thank you screen
    const savedAnswers = sessionStorage.getItem("surveyData");
    const answerData = savedAnswers ? JSON.parse(savedAnswers) : {};

    // Find the "Are you shopping for new insurance?" question
    const shoppingQuestionId = "SQUE_ZoWFs5rGOV";
    const shoppingAnswer = answerData[shoppingQuestionId]?.value;

    // Show thank you screen if explicitly saved or if the "No" answer is present
    return savedThankYou === "true" || shoppingAnswer === "No";
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Function to check for previous responses
  const checkPreviousResponses = async (token) => {
    try {
      if (!token) return { hasPreviousResponse: false };

      // Fetch previous responses
      const responseData = await SurveyService.getSurveyResponsesBySurveyId(
        surveyId,
        token
      );

      // If we have completed responses
      if (responseData && responseData.dateCompleted) {
        setPreviousResponses(responseData);
        return { hasPreviousResponse: true, previousResponses: responseData };
      }

      return { hasPreviousResponse: false };
    } catch (err) {
      console.log("No previous responses found or error checking:", err);
      return { hasPreviousResponse: false };
    }
  };

  // Function to convert server response format to component answers format
  const convertResponsesFormatToAnswers = (responseData, surveyTemplate) => {
    if (!responseData || !responseData.responses || !surveyTemplate) {
      return {};
    }

    const convertedAnswers = {};

    responseData.responses.forEach((response) => {
      // Find the corresponding question in the survey template
      const question = surveyTemplate.questions.find(
        (q) => q.id === response.questionId
      );
      if (!question) return;

      const questionId = response.questionId;

      switch (response.responseType) {
        case "TEXT":
          convertedAnswers[questionId] = {
            value: response.answer || "",
            questionId,
          };
          break;

        case "CHOICE":
          if (question.multiSelect) {
            // Multi-select question
            const values = response.selections.map((s) => s.optionValue);
            const answerIds = response.selections.map((s) => s.id);

            convertedAnswers[questionId] = {
              values,
              answerIds,
              questionId,
            };
          } else {
            // Single-select question
            if (response.selections && response.selections.length > 0) {
              // Find the option in the question to get its text
              const option = question.options.find(
                (opt) => opt.id === response.selections[0].choiceOptionId
              );

              convertedAnswers[questionId] = {
                value: response.selections[0].optionValue,
                answerId: response.selections[0].choiceOptionId, // Use choiceOptionId, not id
                text: option?.text || "",
                questionId,
              };
            }
          }
          break;

        default:
          console.warn(`Unsupported response type: ${response.responseType}`);
      }
    });

    return convertedAnswers;
  };

  // Fetch survey data on component mount
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        setIsLoading(true);

        // Get Firebase token (if user is authenticated)
        let token = null;
        let previousResponses = { hasPreviousResponse: false };
        try {
          token = await getIdToken();
          if (token == null) {
            return;
          }
        } catch (e) {
          //console.warn("check outside No authentication token available:", e);
          return;
        }

        try {
          if (user && token) {
            // Check for previous responses if user is authenticated
            previousResponses = await checkPreviousResponses(token);

            // Set read-only mode if we have previous responses
            if (previousResponses.hasPreviousResponse) {
              setIsReadOnly(true);
            }
          }
        } catch (e) {
          //console.warn("No authentication token available:", e);
          // Continue without token for this initial survey
          console.error(e);
        }

        // Fetch survey data from API or use fallback to embedded data
        let surveyTemplate;
        try {
          surveyTemplate = await SurveyService.getSurveyById(surveyId, token);
        } catch (err) {
          console.warn(
            "Error fetching survey from API, using embedded data:",
            err
          );
          // Use embedded fallback data from paste.txt
        }

        // Sort questions by displayOrder
        const questions = [...surveyTemplate.questions].sort(
          (a, b) => a.displayOrder - b.displayOrder
        );
        setSurveyData(surveyTemplate);
        setSortedQuestions(questions);

        // Track survey start (only for new users)
        if (!previousResponses.hasPreviousResponse) {
          trackSurveyStart(surveyTemplate.name || 'onboarding_survey');
        }

        // If we have previous responses, now we can properly map them
        if (
          previousResponses.hasPreviousResponse &&
          previousResponses.previousResponses &&
          surveyTemplate
        ) {
          const convertedAnswers = convertResponsesFormatToAnswers(
            previousResponses.previousResponses,
            surveyTemplate
          );

          setAnswers(convertedAnswers);
        }

        setIsLoading(false);
      } catch (err) {
        console.error("Error loading survey:", err);
        setErrors((prev) => ({
          ...prev,
          general: "Failed to load survey questions. Please try again.",
        }));
        setIsLoading(false);
      }
    };

    fetchSurvey();
  }, [surveyId, getIdToken, user]);

  // Save current question index to sessionStorage whenever it changes
  useEffect(() => {
    // Only save in sessionStorage if not in read-only mode
    if (!isReadOnly) {
      sessionStorage.setItem(
        "surveyCurrentQuestionIndex",
        currentQuestionIndex.toString()
      );
    }
  }, [currentQuestionIndex, isReadOnly]);

  // Save answers to sessionStorage whenever they change
  useEffect(() => {
    // Only save in sessionStorage if not in read-only mode
    if (!isReadOnly && Object.keys(answers).length > 0) {
      sessionStorage.setItem("surveyData", JSON.stringify(answers));
    }
  }, [answers, isReadOnly]);

  const validateCurrentQuestion = () => {
    // In read-only mode, validation always passes
    if (isReadOnly) return true;

    const currentQuestion = sortedQuestions[currentQuestionIndex];
    if (!currentQuestion) return true;

    // If the question is required and doesn't have an answer, it's invalid
    if (/*currentQuestion.required &&*/ !answers[currentQuestion.id]) {
      setErrors({
        ...errors,
        [currentQuestion.id]: "This question requires an answer",
      });
      return false;
    }

    // Clear any errors for this question
    setErrors({
      ...errors,
      [currentQuestion.id]: null,
    });
    return true;
  };

  const handleSingleAnswer = (questionId, value, answerId) => {
    // Don't process changes in read-only mode
    if (isReadOnly) return;

    // Clear any errors for this question
    setErrors({
      ...errors,
      [questionId]: null,
    });

    const question = sortedQuestions.find((q) => q.id === questionId);
    const option = question?.options?.find((o) => o.id === answerId);

    // Track the question answer
    trackSurveyQuestion(
      surveyData?.name || 'onboarding_survey',
      sortedQuestions.indexOf(question) + 1,
      question?.prompt,
      value
    );

    // Store the answer
    const newAnswers = {
      ...answers,
      [questionId]: {
        type: question?.type || "CHOICE",
        value,
        answerId,
        optionId: answerId,
        text: option?.text || "",
        optionValue: value,
        questionId,
      },
    };

    setAnswers(newAnswers);
  };

  const handleComplete = async () => {
    const startTime = Date.now();
    
    // In read-only mode, just call onComplete with the existing answers

    if (isReadOnly) {
      trackButtonClick('survey_continue_readonly', {
        survey_name: surveyData?.name || 'onboarding_survey'
      });
      onComplete({
        answers: previousResponses.responses,
        completedAt: previousResponses.dateCompleted,
        readOnly: true,
      });
      return;
    }

    trackButtonClick('survey_complete', {
      survey_name: surveyData?.name || 'onboarding_survey',
      total_questions: sortedQuestions.length,
      answered_questions: Object.keys(answers).length
    });

    if (!validateCurrentQuestion()) return;

    setIsLoading(true);
    // Format the answers for API submission
    const formattedResponses = Object.entries(answers)
      .map(([questionId, answer]) => {
        const question = sortedQuestions.find((q) => q.id === questionId);
        if (question && question.type === "TEXT") {
          return {
            type: "TEXT",
            questionId,
            answer: answer.value || "",
          };
        }

        if (question && question.type === "CHOICE") {
          return {
            type: "CHOICE",
            questionId,
            selections: [
              {
                choiceOptionId: answer.answerId,
                text: answer.text || "",
                value: answer.value,
              },
            ],
          };
        }

        return null;
      })
      .filter(Boolean);

console.log("SUBMIT SUV22E" + isReadOnly);

    // Create the complete survey response object
    const surveyResponse = {
      userId: "",
      surveyTemplateId: surveyId,
      responses: formattedResponses,
    };
    // If user is authenticated, submit the survey now
    if (user) {
      const token = await getIdToken();
      await SurveyService.submitSurveyResponses(
        surveyId,
        user.uid,
        formattedResponses,
        token
      );

      // Mark survey as submitted in session storage
      sessionStorage.setItem("surveySubmitted", "true");
    } else {
      // Store formatted responses for later submission after authentication
      sessionStorage.setItem(
        "formattedSurveyResponses",
        JSON.stringify(formattedResponses)
      );
    }
    
    // Track survey completion
    const completionTime = Date.now() - startTime;
    trackSurveyComplete(
      surveyData?.name || 'insurance_interest_survey',
      sortedQuestions.length,
      completionTime
    );
    
    setIsLoading(false);
    onComplete(answers);
  };

  // Render thank you screen when user indicates they don't want insurance

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        <span className="ml-3">Synchronizing survey data...</span>
      </div>
    );
  }

  // Error state
  if (errors.general) {
    return (
      <div className="bg-red-50 border border-red-200 p-4 rounded-lg text-red-800">
        <h3 className="font-medium">Error</h3>
        <p>{errors.general}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-red-600 hover:text-red-800"
        >
          Retry
        </button>
      </div>
    );
  }

  // Thank you screen
  // if (showThankYou) {
  //   return renderThankYouScreen();
  // }

  // No questions available
  if (sortedQuestions.length === 0) {
    return <div>No survey questions available</div>;
  }

  // Get the current question
  const currentQuestion = sortedQuestions[currentQuestionIndex];

  // Calculate progress

  // Check if current question has been answered

  // Check if all required questions are answered
  const allRequiredQuestionsAnswered = sortedQuestions
    .filter((q) => q.required)
    .every((q) => answers[q.id] !== undefined);

  return (
    <div className="p-4 space-y-6">
      {/* Back button if onBack is provided */}
      {/* {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>
      )} */}
      {/* Headline and body copy */}
      <div className="text-center mb-6">
        <h2 className="text-xl uppercase font-bold text-gray-900">
          Ownli Insurance Profile
        </h2>
        <p className="text-darkGrey mt-4">
          A new way to find the best coverage, specifically for your needs.
        </p>
      </div>
      {/* Read-only banner */}
      {isReadOnly && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <Lock className="h-5 w-5 text-blue-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                This survey was completed on{" "}
                {new Date(previousResponses.dateCompleted).toLocaleDateString()}
                . You can review your answers but cannot modify them.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {/* <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
        <div
          className="bg-blue-600 h-full transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div> */}

      {/* Step indicator */}
      {/* <div className="flex justify-between text-sm text-gray-600 px-1">
        <span>
          Step {currentQuestionIndex + 1} of {sortedQuestions.length}
        </span>
        <span>{Math.round(progress)}% Complete</span>
      </div> */}

      <div className="space-y-6">
        {sortedQuestions.map((question) => (
          <div key={question.id} className="!mb-10">
            <div className="mb-8">
              <span className="font-bold text-base">
                {/* {sortedQuestions.indexOf(question) + 1}.  */}
                {question.prompt}
                {/* {question.required && (
                  <span className="text-red-500 ml-1">*</span>
                )} */}
              </span>
              {/* {question.helpText && (
                <div className="text-sm text-gray-500 mt-1">
                  {question.helpText}
                </div>
              )} */}
            </div>
            <div className="flex flex-col gap-8 ml-1">
              {question.type === "CHOICE" && (
                <>
                  {[...question.options]
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((option) => {
                      const selected =
                        answers[question.id]?.value === option.value;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() =>
                            handleSingleAnswer(
                              question.id,
                              option.value,
                              option.id
                            )
                          }
                          className="flex items-center gap-3 focus:outline-none"
                          disabled={isReadOnly}
                        >
                          <span
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              selected ? "border-violet-500" : "border-gray-400"
                            } ${isReadOnly ? "bg-gray-100" : "bg-white"}`}
                          >
                            {selected && (
                              <span className="w-3 h-3 rounded-full bg-violet-500 block" />
                            )}
                          </span>
                          <span
                            className={`text-base ${
                              selected
                                ? "text-violet-500"
                                : isReadOnly
                                ? "text-gray-700"
                                : "text-black"
                            }`}
                          >
                            {option.text}
                          </span>
                        </button>
                      );
                    })}
                </>
              )}
            </div>
            {errors[question.id] && (
              <div className="mt-4 text-red-500 text-sm">
                {errors[question.id]}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Complete button */}
      <div className="flex flex-col">
        <button
          onClick={handleComplete}
          disabled={isLoading || (!isReadOnly && !allRequiredQuestionsAnswered)}
          className={`w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors disabled:bg-none disabled:bg-gray-300`}
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Processing...</span>
            </>
          ) : (
            <>
              {/* <span>{isReadOnly ? "Continue" : "Complete"}</span> */}
              <span>Next</span>
              {/* <ArrowRight className="w-4 h-4" /> */}
            </>
          )}
        </button>
        <button
          onClick={onBack}
          className="w-full uppercase h-12 border-2 border-darkGrey font-bold text-darkGrey px-4 rounded-xl transition-colors mt-4"
        >
          Back
        </button>
      </div>

      {/* Only show Reset button in non-read-only mode */}
      {/* {!isReadOnly && (
        <button
          onClick={onReset}
          className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 text-sm transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Start Over
        </button>
      )} */}
    </div>
  );
};

export default Survey;
